import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { countEmptyCells, hasHeaderRow, hasTableStyle, readTables } from "@/office/word/tables";

export const lektionTabellenAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Eine Übersichtstabelle",
  summary: "Struktur, Kopfzeile und Gestaltung in einer fertigen Tabelle vereinen.",
  durationMinutes: 10,
  learningGoals: [
    "Eine Tabelle vollständig und regelkonform aufbauen",
    "Struktur und Gestaltung sauber trennen",
  ],
  steps: [
    {
      id: "uebersichtstabelle",
      title: "Die Tabelle aufbauen",
      explanation: [
        "Baue eine Übersichtstabelle, wie sie in einem Bericht stehen könnte – zum " +
          "Beispiel eine Kostenaufstellung oder einen Projektzeitplan.",
        "Achte darauf, dass die Struktur stimmt, bevor du dich um das Aussehen kümmerst: " +
          "erst Zeilen und Spalten, dann Überschriftenzeile, dann die Formatvorlage.",
      ],
      keyPoints: [
        "Erst Struktur, dann Gestaltung.",
        "Keine leeren Zellen – lieber ein Strich oder „entfällt“.",
        "Überschriftenzeile setzen, nicht nur fett formatieren.",
      ],
      task: {
        instruction:
          "Erstelle eine Tabelle mit mindestens 3 Spalten und 5 Zeilen, vollständig " +
          "ausgefüllt, mit gesetzter Überschriftenzeile und einer zugewiesenen " +
          "Tabellenformatvorlage.",
        hint: "Leere Zellen sind der häufigste Stolperstein – trage überall etwas ein, notfalls einen Gedankenstrich.",
        check: async () => {
          const tabellen = await readTables();
          if (tabellen.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Tabelle.",
              "Einfügen → Tabelle, dann die Größe aufziehen.",
            );
          }

          const tabelle = tabellen[0];
          const leer = countEmptyCells(tabelle);

          return fromCriteria(
            [
              criterion(`Mindestens 3 Spalten (gefunden: ${tabelle.columnCount})`, tabelle.columnCount >= 3),
              criterion(`Mindestens 5 Zeilen (gefunden: ${tabelle.rowCount})`, tabelle.rowCount >= 5),
              criterion(`Keine leeren Zellen (gefunden: ${leer})`, leer === 0),
              criterion("Überschriftenzeile ist gesetzt", hasHeaderRow(tabelle)),
              criterion(
                `Eine Tabellenformatvorlage ist zugewiesen (erkannt: ${
                  tabelle.styleName || "keine"
                })`,
                hasTableStyle(tabelle),
              ),
            ],
            {
              success: "Eine saubere Tabelle – strukturiert, vollständig und einheitlich gestaltet.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Die Überschriftenzeile ist eine eigene Einstellung unter Layout → „Überschriften wiederholen“, unabhängig von der Formatvorlage.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Tabellen folgen demselben Grundsatz wie alles andere in diesem Kurs: Struktur " +
          "und Aussehen sind zwei verschiedene Dinge. Die Überschriftenzeile ist Struktur, " +
          "die Formatvorlage ist Aussehen – und beides gehört an die dafür vorgesehene " +
          "Stelle statt in fetten Text und selbst gezogene Rahmen.",
      ],
      keyPoints: [
        "Tabellen für Daten, nicht fürs Layout.",
        "Überschriftenzeile markieren, nicht nur fett setzen.",
        "Gestaltung über Tabellenformatvorlagen.",
        "Beim Sortieren den richtigen Datentyp wählen.",
      ],
    },
  ],
};
